import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, CheckCircle2, XCircle, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";

/**
 * ResetWachtwoord — pagina waar gebruiker klikt vanuit reset-email.
 * URL: /reset-wachtwoord/:token
 *
 * Flow:
 *   1. On mount: valideert token via GET /auth/wachtwoord-reset/token/:token
 *      → success: toont formulier met masked email
 *      → 404/400: toont fout + link naar wachtwoord-vergeten opnieuw
 *   2. Submit: POST /auth/wachtwoord-reset {token, nieuw_wachtwoord}
 *      → toont succes → redirect naar login na 2 sec
 */
export default function ResetWachtwoord() {
  const { token } = useParams();
  const nav = useNavigate();
  const [checking, setChecking] = useState(true);
  const [emailMasked, setEmailMasked] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "BPTplus · Wachtwoord opnieuw instellen";
    (async () => {
      try {
        const { data } = await api.get(`/auth/wachtwoord-reset/token/${token}`);
        setEmailMasked(data.email_masked || null);
      } catch (err) {
        setTokenError(formatErr(err.response?.data?.detail) || "Ongeldige link");
      }
      setChecking(false);
    })();
  }, [token]);

  const checks = validatePw(pw1);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!checks.ok) {
      toast.error("Wachtwoord voldoet nog niet aan de eisen");
      return;
    }
    if (pw1 !== pw2) {
      toast.error("Wachtwoorden komen niet overeen");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/wachtwoord-reset", { token, nieuw_wachtwoord: pw1 });
      setDone(true);
      toast.success("Wachtwoord bijgewerkt — u kunt nu inloggen");
      setTimeout(() => nav("/login"), 2200);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Reset mislukt");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-12" data-testid="reset-page">
      {/* Links brand-panel — hetzelfde als login */}
      <aside
        className="hidden lg:flex lg:col-span-5 xl:col-span-6 relative overflow-hidden text-white flex-col justify-between p-12 xl:p-16"
        style={{ background: "linear-gradient(160deg, #0b1120 0%, #111827 60%, #0f172a 100%)" }}
      >
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "56px 56px" }} aria-hidden />
        <div className="absolute -top-24 -left-32 h-64 w-96 rotate-[-16deg] opacity-25"
             style={{ background: "linear-gradient(90deg, hsl(var(--bpt-red)) 0%, transparent 100%)" }} aria-hidden />

        <div className="relative z-10">
          <img src="/bpt-logo.webp" alt="Brandpreventie Techniek"
               className="h-12 w-auto object-contain brightness-0 invert opacity-95" />
        </div>

        <div className="relative z-10 space-y-6 max-w-md">
          <div className="flex items-center gap-2">
            <span className="inline-block h-[2px] w-8 bg-[hsl(var(--bpt-red))]" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-white/60 font-semibold">
              BPTplus · Wachtwoordherstel
            </span>
          </div>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tighter leading-[1.05]">
            Kies een nieuw<br />
            <span className="text-white/50">wachtwoord.</span>
          </h1>
          <p className="text-white/60 text-sm leading-relaxed max-w-sm">
            Gebruik een uniek wachtwoord van minimaal 8 tekens dat u nergens anders gebruikt. Vermijd persoonlijke gegevens zoals uw naam of geboortedatum.
          </p>
        </div>

        <div className="relative z-10 text-[10px] uppercase tracking-widest text-white/50">
          © {new Date().getFullYear()} · Brandpreventie Techniek B.V.
        </div>
      </aside>

      {/* Rechts formulier */}
      <section className="col-span-1 lg:col-span-7 xl:col-span-6 bg-[#fafbfc] flex items-center justify-center p-6 sm:p-10 lg:p-14">
        <div className="w-full max-w-md">
          {checking ? (
            <div className="text-center py-20 text-slate-500" data-testid="reset-checking">
              <Loader2 className="h-6 w-6 mx-auto animate-spin mb-3" />
              Link controleren…
            </div>
          ) : tokenError ? (
            <div data-testid="reset-error">
              <div className="inline-flex items-center gap-2 mb-3 text-red-600 text-xs font-semibold uppercase tracking-widest">
                <XCircle className="h-4 w-4" /> Link ongeldig
              </div>
              <h2 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">
                Deze link werkt niet meer
              </h2>
              <p className="text-slate-600 mt-3 text-sm leading-relaxed">
                {tokenError}. Reset-links zijn 60 minuten geldig en éénmalig te gebruiken.
              </p>
              <div className="mt-6">
                <Link to="/wachtwoord-vergeten">
                  <Button className="bg-[hsl(var(--bpt-red))] hover:bg-[hsl(var(--bpt-red-dark))]" data-testid="reset-new-link">
                    Nieuwe reset-link aanvragen
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/login" className="block mt-3 text-xs text-slate-500 hover:text-slate-800">
                  ← Terug naar inloggen
                </Link>
              </div>
            </div>
          ) : done ? (
            <div data-testid="reset-done">
              <div className="inline-flex items-center gap-2 mb-3 text-emerald-600 text-xs font-semibold uppercase tracking-widest">
                <CheckCircle2 className="h-4 w-4" /> Gelukt
              </div>
              <h2 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">
                Uw wachtwoord is bijgewerkt
              </h2>
              <p className="text-slate-600 mt-3 text-sm leading-relaxed">
                U wordt automatisch doorgestuurd naar de inlog-pagina.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-4 w-4 text-[hsl(var(--bpt-red))]" />
                  <span className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold">
                    Wachtwoord opnieuw instellen
                  </span>
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
                  Nieuw wachtwoord
                </h2>
                {emailMasked && (
                  <p className="text-slate-500 text-sm mt-2 leading-relaxed">
                    Voor account: <span className="font-mono text-slate-700" data-testid="reset-email-masked">{emailMasked}</span>
                  </p>
                )}
              </div>

              <form onSubmit={onSubmit} data-testid="reset-form" className="space-y-5">
                <div>
                  <Label htmlFor="pw1" className="text-slate-700 text-xs font-semibold uppercase tracking-wider">
                    Nieuw wachtwoord
                  </Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="pw1"
                      type={showPw ? "text" : "password"}
                      autoComplete="new-password"
                      value={pw1}
                      onChange={(e) => setPw1(e.target.value)}
                      required
                      autoFocus
                      className="pl-9 pr-10 h-11 bg-white border-slate-200"
                      placeholder="Minimaal 8 tekens"
                      data-testid="reset-pw1"
                    />
                    <button type="button" onClick={() => setShowPw((s) => !s)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                            aria-label={showPw ? "Verberg" : "Toon"} data-testid="reset-toggle-pw">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="pw2" className="text-slate-700 text-xs font-semibold uppercase tracking-wider">
                    Herhaal wachtwoord
                  </Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="pw2"
                      type={showPw ? "text" : "password"}
                      autoComplete="new-password"
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      required
                      className="pl-9 h-11 bg-white border-slate-200"
                      placeholder="Herhaal nieuw wachtwoord"
                      data-testid="reset-pw2"
                    />
                  </div>
                </div>

                {/* Live checks */}
                <div className="rounded-md border border-slate-200 bg-white p-3 space-y-1.5" data-testid="reset-checks">
                  <PwCheck ok={checks.length} label="Minimaal 8 tekens" />
                  <PwCheck ok={checks.letter} label="Bevat minimaal 1 letter" />
                  <PwCheck ok={checks.digit} label="Bevat minimaal 1 cijfer" />
                  <PwCheck ok={pw1.length > 0 && pw1 === pw2} label="Beide velden komen overeen" />
                </div>

                <Button
                  type="submit"
                  disabled={busy || !checks.ok || pw1 !== pw2}
                  className="group w-full h-11 bg-[hsl(var(--bpt-red))] hover:bg-[hsl(var(--bpt-red-dark))] text-white font-semibold text-sm tracking-wide"
                  data-testid="reset-submit"
                >
                  {busy ? "Bezig…" : (
                    <span className="inline-flex items-center gap-2">
                      Wachtwoord bijwerken
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  )}
                </Button>

                <Link to="/login" className="block text-center mt-2 text-xs text-slate-500 hover:text-slate-800">
                  ← Terug naar inloggen
                </Link>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function validatePw(pw) {
  const length = pw.length >= 8;
  const letter = /[A-Za-z]/.test(pw);
  const digit = /\d/.test(pw);
  return { length, letter, digit, ok: length && letter && digit };
}

function PwCheck({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-600" : "text-slate-400"}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {label}
    </div>
  );
}
